import { useCallback, useEffect, useState } from 'react';
import { FIRST_LOGIN_VIDEO_FEATURE_FLAG, FIRST_LOGIN_VIDEO_VERIFICATION_SOURCE_KEY } from '../constants/firstLoginVideo';
import { FeatureFlags } from '../types/featureFlags';
import { User } from '../types/user';

const hasEnglishBrowserLanguage = (): boolean => {
  const browserLanguages = [navigator.language, ...(navigator.languages || [])]
    .filter((language): language is string => Boolean(language));

  return browserLanguages.some((language) => language.toLowerCase().startsWith('en'));
};

export type UseFirstLoginVideoParams = {
  user?: User;
  featureFlags?: FeatureFlags;
  isUserLoading?: boolean;
  isFeatureFlagsLoading?: boolean;
};

export type UseFirstLoginVideoResult = {
  isOpen: boolean;
  handleClose: () => void;
};

export const useFirstLoginVideo = ({
  user,
  featureFlags,
  isUserLoading = false,
  isFeatureFlagsLoading = false,
}: UseFirstLoginVideoParams): UseFirstLoginVideoResult => {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isUserLoading || isFeatureFlagsLoading) {
      return;
    }

    if (!user?.uuid) {
      setIsOpen(false);
      return;
    }

    const shouldOpenFromVerification = sessionStorage.getItem(FIRST_LOGIN_VIDEO_VERIFICATION_SOURCE_KEY) === 'true';
    if (!shouldOpenFromVerification) {
      setIsOpen(false);
      return;
    }

    const isFirstLoginVideoFeatureEnabled = featureFlags?.[FIRST_LOGIN_VIDEO_FEATURE_FLAG] === true;
    if (!isFirstLoginVideoFeatureEnabled) {
      setIsOpen(false);
      sessionStorage.removeItem(FIRST_LOGIN_VIDEO_VERIFICATION_SOURCE_KEY);
      return;
    }

    const isUsUser = user.details.countryCode === 'US';
    const isEnglishBrowser = hasEnglishBrowserLanguage();
    setIsOpen(isUsUser && isEnglishBrowser);

    // Consume marker so direct reloads do not re-open the video.
    sessionStorage.removeItem(FIRST_LOGIN_VIDEO_VERIFICATION_SOURCE_KEY);
  }, [featureFlags, isFeatureFlagsLoading, isUserLoading, user?.details.countryCode, user?.uuid]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
    sessionStorage.removeItem(FIRST_LOGIN_VIDEO_VERIFICATION_SOURCE_KEY);
  }, []);

  return {
    isOpen,
    handleClose,
  };
};
